import React, { useState } from 'react';
import { SystemConfig } from '../../data/types';

interface FeatureTogglesTabProps {
  config: SystemConfig;
  onSave: (updatedConfig: Partial<SystemConfig>) => void;
  saving: boolean;
}

const FeatureTogglesTab: React.FC<FeatureTogglesTabProps> = ({ config, onSave, saving }) => {
  const [toggles, setToggles] = useState({
    smartDifficulty: config.featureToggles.smartDifficulty,
    leaderboards: config.featureToggles.leaderboards,
    achievements: config.featureToggles.achievements,
    parentPortal: config.featureToggles.parentPortal,
    aiAssistant: config.featureToggles.aiAssistant
  });

  const handleSave = () => {
    const updatedConfig: Partial<SystemConfig> = {
      featureToggles: toggles
    };
    
    onSave(updatedConfig);
  };

  const handleToggleChange = (feature: keyof typeof toggles) => {
    setToggles({
      ...toggles,
      [feature]: !toggles[feature]
    });
  };

  // Feature descriptions
  const featureDescriptions = {
    smartDifficulty: 'Automatically adjust content difficulty based on student performance',
    leaderboards: 'Display leaderboards to motivate students through friendly competition',
    achievements: 'Award badges and achievements for completing learning milestones',
    parentPortal: 'Allow parents to view their child\'s progress and performance',
    aiAssistant: 'Provide AI-powered hints and assistance to students'
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">Feature Toggles</h2>
      <p className="text-gray-600 mb-6">
        Enable or disable platform features district-wide. These settings affect all schools and users.
      </p>
      
      <div className="space-y-6 mb-8">
        {/* Smart Difficulty */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Smart Difficulty</h3>
              <p className="text-sm text-gray-500">{featureDescriptions.smartDifficulty}</p>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-sm text-gray-500">{toggles.smartDifficulty ? 'Enabled' : 'Disabled'}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={toggles.smartDifficulty}
                  onChange={() => handleToggleChange('smartDifficulty')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
        
        {/* Leaderboards */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Leaderboards</h3>
              <p className="text-sm text-gray-500">{featureDescriptions.leaderboards}</p>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-sm text-gray-500">{toggles.leaderboards ? 'Enabled' : 'Disabled'}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={toggles.leaderboards}
                  onChange={() => handleToggleChange('leaderboards')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
        
        {/* Achievements */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Achievements</h3>
              <p className="text-sm text-gray-500">{featureDescriptions.achievements}</p>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-sm text-gray-500">{toggles.achievements ? 'Enabled' : 'Disabled'}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={toggles.achievements}
                  onChange={() => handleToggleChange('achievements')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
        
        {/* Parent Portal */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">Parent Portal</h3>
              <p className="text-sm text-gray-500">{featureDescriptions.parentPortal}</p>
              {!toggles.parentPortal && (
                <p className="text-xs text-blue-500 mt-1">Coming Soon</p>
              )}
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-sm text-gray-500">{toggles.parentPortal ? 'Enabled' : 'Disabled'}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={toggles.parentPortal}
                  onChange={() => handleToggleChange('parentPortal')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
        
        {/* AI Assistant */}
        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-700">AI Assistant</h3>
              <p className="text-sm text-gray-500">{featureDescriptions.aiAssistant}</p>
            </div>
            <div className="flex items-center">
              <span className="mr-3 text-sm text-gray-500">{toggles.aiAssistant ? 'Enabled' : 'Disabled'}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={toggles.aiAssistant}
                  onChange={() => handleToggleChange('aiAssistant')}
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className={`px-4 py-2 rounded text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
          }`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Feature Settings'}
        </button>
      </div>
    </div>
  );
};

export default FeatureTogglesTab;
